import React, {useState} from 'react';
import T from 'prop-types';
import {useSelector} from 'react-redux';
import moment from 'moment';
import {toast} from 'react-toastify';
import {saveAs} from 'file-saver';
import {
  CardSubtitle,
  CardTitle,
  CashbackCard,
  DownloadButton,
  EmptyOrders,
  OrderRow,
  OrdersTable,
  OrderTypeBadge,
  TableHeader
} from 'routes/App/cashback/styles';
import IconDownload from 'routes/App/cashback/icons/IconDownload';
import IconCashbackOrdersEmpty from 'routes/App/cashback/icons/IconCashbackOrdersEmpty';
import {getXlsReportsApi} from 'helpers/services/api/reports';
import {REPORT_TYPES} from 'helpers/constants/reports';

const formatCurrency = (value) =>
  `$${(value || 0).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })}`;

const formatDate = (dateString) => {
  if (!dateString) return '';
  const now = moment().utc().startOf('day');
  const date = moment(dateString).utc().startOf('day');
  const diff = now.diff(date, 'days');
  if (diff === 0) return 'Today';
  if (diff === 1) return 'Yesterday';
  return date.format('MMM D');
};

const getInitials = (name) => {
  if (!name) return '';
  const parts = name.trim().split(/\s+/);
  return parts
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase();
};

const getOrderTransactions = (order) =>
  (order.transactions || []).filter((transaction) => transaction.type);

const captureCashbackEvent = (eventName, properties) => {
  if (window?.posthog) {
    window.posthog.capture(eventName, properties);
  }
};

const RecentOrders = ({orders}) => {
  const hasOrders = orders && orders.length > 0;
  const locationId = useSelector((state) => state.user.locationId);
  const activeLocation = useSelector((state) => state.user.activeLocation);
  const [downloadLoading, setDownloadLoading] = useState(false);

  const handleDownloadFullReport = async () => {
    if (!locationId || downloadLoading) return;

    setDownloadLoading(true);
    try {
      const resp = await getXlsReportsApi(
        null,
        REPORT_TYPES.CASHBACK_USAGE,
        locationId,
        [null, null]
      );

      const locationName = (
        activeLocation?.FullName ||
        activeLocation?.BusinessName ||
        'location'
      )
        .replace(/[<>:"/\\|?*\s]+/g, '_')
        .replace(/_+/g, '_');
      const fileName = `cashback_usage_${locationName}_${moment().format(
        'YYYY-MM-DD'
      )}.xlsx`;

      saveAs(new Blob([resp.data], {type: ''}), fileName);

      captureCashbackEvent('cashback_report_downloaded', {
        report_type: 'full_usage',
        location_id: locationId,
        location_name:
          activeLocation?.BusinessName || activeLocation?.FullName || ''
      });
    } catch (error) {
      console.error('Cashback usage export failed', error);
      toast.error('Failed to download cashback usage report');
    } finally {
      setDownloadLoading(false);
    }
  };

  return (
    <CashbackCard>
      <TableHeader>
        <div>
          <CardTitle>Recent cashback orders</CardTitle>
          <CardSubtitle>Latest orders with Cashback activity.</CardSubtitle>
        </div>
        {locationId && (
          <DownloadButton
            onClick={handleDownloadFullReport}
            disabled={downloadLoading}
          >
            <IconDownload />
            <span>{downloadLoading ? 'Downloading…' : 'Download'}</span>
          </DownloadButton>
        )}
      </TableHeader>

      {hasOrders ? (
        <OrdersTable>
          {orders.map((order, idx) => {
            const transactions = getOrderTransactions(order);
            const transactionsKey = transactions
              .map((transaction) => `${transaction.type}-${transaction.amount}`)
              .join('-');

            return (
              <OrderRow
                // eslint-disable-next-line react/no-array-index-key
                key={`${order.customerName}-${order.date}-${transactionsKey}-${idx}`}
              >
                <div className='avatar'>{getInitials(order.customerName)}</div>
                <div className='name'>{order.customerName}</div>
                <div className='order-info order-amount'>
                  <div className='order-credit-lines'>
                    {transactions.map((transaction, transactionIdx) => (
                      <div
                        className='order-credit-line'
                        // eslint-disable-next-line react/no-array-index-key
                        key={`${transaction.type}-${transactionIdx}`}
                      >
                        {transaction.type}{' '}
                        <b>{formatCurrency(transaction.amount)}</b> credit
                      </div>
                    ))}
                  </div>
                </div>
                <div className='order-types'>
                  {transactions.map((transaction, transactionIdx) => (
                    <OrderTypeBadge
                      className={transaction.type}
                      // eslint-disable-next-line react/no-array-index-key
                      key={`${transaction.type}-${transactionIdx}`}
                    >
                      {transaction.type}
                    </OrderTypeBadge>
                  ))}
                </div>
                <div className='order-date'>{formatDate(order.date)}</div>
              </OrderRow>
            );
          })}
        </OrdersTable>
      ) : (
        <EmptyOrders>
          <div className='iconBg'>
            <IconCashbackOrdersEmpty />
          </div>
          <div className='title'>No cashback orders yet</div>
          <div className='desc'>
            They&apos;ll appear here as customers start earning and redeeming.
          </div>
        </EmptyOrders>
      )}
    </CashbackCard>
  );
};

RecentOrders.propTypes = {
  orders: T.arrayOf(
    T.shape({
      customerName: T.string,
      orderedAmount: T.number,
      transactions: T.arrayOf(
        T.shape({
          type: T.string,
          amount: T.number
        })
      ),
      date: T.string
    })
  )
};

RecentOrders.defaultProps = {
  orders: []
};

export default RecentOrders;
